import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
    Plus, Pencil, Trash2, LogOut, Leaf, Download, Reply, Users, Sprout, BookOpen, ImageIcon,
    Calendar as CalIcon, MessageSquare, Mail, Settings as SettingsIcon, FolderKanban, GaugeCircle,
} from "lucide-react";

// --- Reusable mutation helpers ---
function useEntity(path, queryKey) {
    const qc = useQueryClient();
    const list = useQuery({ queryKey: [queryKey], queryFn: () => api.get(path).then((r) => r.data) });
    const create = useMutation({
        mutationFn: (data) => api.post(path, data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
    });
    const update = useMutation({
        mutationFn: ({ id, data }) => api.put(`${path}/${id}`, data).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
    });
    const remove = useMutation({
        mutationFn: (id) => api.delete(`${path}/${id}`).then((r) => r.data),
        onSuccess: () => qc.invalidateQueries({ queryKey: [queryKey] }),
    });
    return { list, create, update, remove };
}

function csvDownload(filename, rows) {
    if (!rows.length) return;
    const cols = Object.keys(rows[0]);
    const header = cols.join(",");
    const body = rows
        .map((r) =>
            cols
                .map((c) => {
                    const v = r[c];
                    if (v == null) return "";
                    const s = Array.isArray(v) ? v.join("; ") : String(v).replace(/\n/g, " ");
                    return `"${s.replace(/"/g, '""')}"`;
                })
                .join(",")
        )
        .join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
}

// ============================================================================
// SECTIONS
// ============================================================================

function StatsSection() {
    const { data } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/admin/stats").then((r) => r.data) });
    const visits = useQuery({ queryKey: ["visits"], queryFn: () => api.get("/visit").then((r) => r.data) });
    const items = [
        { label: "Trustees", value: data?.trustees, icon: Users },
        { label: "Programs", value: data?.programs, icon: Sprout },
        { label: "Projects", value: data?.projects, icon: FolderKanban },
        { label: "Events", value: data?.events, icon: CalIcon },
        { label: "Gallery", value: data?.gallery, icon: ImageIcon },
        { label: "Blogs", value: data?.blogs, icon: BookOpen },
        { label: "Volunteers", value: data?.volunteers, icon: Users },
        { label: "Contacts", value: data?.contacts, icon: MessageSquare },
        { label: "Newsletter", value: data?.newsletter, icon: Mail },
        { label: "Site visits", value: visits.data?.count, icon: GaugeCircle },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border">
            {items.map((i) => (
                <div key={i.label} className="bg-background p-5">
                    <i.icon className="h-5 w-5 text-secondary mb-3" />
                    <div className="font-display text-3xl">{i.value ?? "—"}</div>
                    <div className="font-eyebrow text-[10px] text-muted-foreground mt-1">{i.label}</div>
                </div>
            ))}
        </div>
    );
}

// Trustees
function TrusteesSection() {
    const e = useEntity("/trustees", "trustees");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const blank = { name: "", role: "Trustee", bio: "", image_url: "", order: 0 };
    const [form, setForm] = useState(blank);
    const startNew = () => { setEditing(null); setForm(blank); setOpen(true); };
    const startEdit = (t) => { setEditing(t); setForm({ name: t.name, role: t.role, bio: t.bio || "", image_url: t.image_url || "", order: t.order || 0 }); setOpen(true); };
    const save = async () => {
        try {
            if (editing) await e.update.mutateAsync({ id: editing.id, data: form });
            else await e.create.mutateAsync(form);
            toast.success("Saved");
            setOpen(false);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    return (
        <div>
            <SectionHeader title="Trustees" count={e.list.data?.length} onNew={startNew} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(e.list.data || []).map((t) => (
                    <div key={t.id} className="bg-card border border-border p-4 flex gap-4">
                        <img src={t.image_url || "https://via.placeholder.com/100"} alt="" className="h-20 w-20 object-cover" />
                        <div className="flex-1">
                            <div className="font-eyebrow text-[10px] text-secondary">{t.role}</div>
                            <div className="font-display text-lg">{t.name}</div>
                            <div className="text-xs text-muted-foreground line-clamp-2">{t.bio}</div>
                            <div className="mt-2 flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => startEdit(t)} data-testid={`edit-trustee-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="outline" onClick={() => { if (window.confirm("Delete?")) e.remove.mutate(t.id); }} data-testid={`delete-trustee-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Trustee</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <FormField label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
                        <FormField label="Role"><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></FormField>
                        <FormField label="Image URL"><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></FormField>
                        <FormField label="Bio"><Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></FormField>
                        <FormField label="Order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></FormField>
                    </div>
                    <DialogFooter><Button onClick={save} data-testid="trustee-save">Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Programs
function ProgramsSection() {
    const e = useEntity("/programs", "programs-admin");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const blank = { category: "environment", title: "", description: "", image_url: "", icon: "leaf", order: 0 };
    const [form, setForm] = useState(blank);
    const startNew = () => { setEditing(null); setForm(blank); setOpen(true); };
    const startEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };
    const save = async () => {
        try {
            if (editing) await e.update.mutateAsync({ id: editing.id, data: form });
            else await e.create.mutateAsync(form);
            toast.success("Saved");
            setOpen(false);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    return (
        <div>
            <SectionHeader title="Programs" count={e.list.data?.length} onNew={startNew} />
            <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border">
                    <thead className="bg-muted/50 font-eyebrow text-[10px] text-left">
                        <tr><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Order</th><th className="p-3"></th></tr>
                    </thead>
                    <tbody>
                        {(e.list.data || []).map((p) => (
                            <tr key={p.id} className="border-t border-border">
                                <td className="p-3">{p.title}</td>
                                <td className="p-3 capitalize text-muted-foreground">{p.category}</td>
                                <td className="p-3">{p.order}</td>
                                <td className="p-3 text-right flex justify-end gap-2">
                                    <Button size="sm" variant="outline" onClick={() => startEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                                    <Button size="sm" variant="outline" onClick={() => { if (window.confirm("Delete?")) e.remove.mutate(p.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Program</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <FormField label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormField>
                        <FormField label="Category">
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="environment">Environment</SelectItem>
                                    <SelectItem value="education">Education</SelectItem>
                                    <SelectItem value="food">Food</SelectItem>
                                    <SelectItem value="social">Social</SelectItem>
                                </SelectContent>
                            </Select>
                        </FormField>
                        <FormField label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormField>
                        <FormField label="Image URL"><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></FormField>
                        <FormField label="Icon key (tree/broom/droplet/graduation/book/lightbulb/utensils/soup/heart-handshake/stethoscope/users/hand-heart/leaf)"><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></FormField>
                        <FormField label="Order"><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></FormField>
                    </div>
                    <DialogFooter><Button onClick={save}>Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Projects
function ProjectsSection() {
    const e = useEntity("/projects", "projects-admin");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const blank = { title: "", description: "", cover_image: "", gallery: [], location: "", start_date: "", end_date: "", status: "ongoing", beneficiaries: "", impact_summary: "", category: "environment" };
    const [form, setForm] = useState(blank);
    const [galleryStr, setGalleryStr] = useState("");
    const startNew = () => { setEditing(null); setForm(blank); setGalleryStr(""); setOpen(true); };
    const startEdit = (p) => { setEditing(p); setForm({ ...p }); setGalleryStr((p.gallery || []).join("\n")); setOpen(true); };
    const save = async () => {
        const data = { ...form, gallery: galleryStr.split("\n").map((x) => x.trim()).filter(Boolean) };
        try {
            if (editing) await e.update.mutateAsync({ id: editing.id, data });
            else await e.create.mutateAsync(data);
            toast.success("Saved");
            setOpen(false);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    return (
        <div>
            <SectionHeader title="Projects" count={e.list.data?.length} onNew={startNew} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(e.list.data || []).map((p) => (
                    <div key={p.id} className="bg-card border border-border">
                        <img src={p.cover_image} alt="" className="w-full aspect-video object-cover" />
                        <div className="p-4">
                            <div className="font-eyebrow text-[10px] text-secondary">{p.status}</div>
                            <div className="font-display text-base mt-1 leading-tight">{p.title}</div>
                            <div className="text-xs text-muted-foreground mt-1">{p.location}</div>
                            <div className="mt-3 flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => startEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button size="sm" variant="outline" onClick={() => { if (window.confirm("Delete?")) e.remove.mutate(p.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Project</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <FormField label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormField>
                        <FormField label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormField>
                        <FormField label="Cover image URL"><Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} /></FormField>
                        <FormField label="Gallery URLs (one per line)"><Textarea rows={3} value={galleryStr} onChange={(e) => setGalleryStr(e.target.value)} /></FormField>
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></FormField>
                            <FormField label="Status">
                                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="ongoing">Ongoing</SelectItem>
                                        <SelectItem value="completed">Completed</SelectItem>
                                        <SelectItem value="upcoming">Upcoming</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormField>
                            <FormField label="Start date"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></FormField>
                            <FormField label="End date"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></FormField>
                            <FormField label="Beneficiaries"><Input value={form.beneficiaries} onChange={(e) => setForm({ ...form, beneficiaries: e.target.value })} /></FormField>
                            <FormField label="Category">
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="environment">Environment</SelectItem>
                                        <SelectItem value="education">Education</SelectItem>
                                        <SelectItem value="food">Food</SelectItem>
                                        <SelectItem value="social">Social</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FormField>
                        </div>
                        <FormField label="Impact summary"><Textarea rows={2} value={form.impact_summary} onChange={(e) => setForm({ ...form, impact_summary: e.target.value })} /></FormField>
                    </div>
                    <DialogFooter><Button onClick={save}>Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Events
function EventsSection() {
    const e = useEntity("/events", "events-admin");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const blank = { name: "", date: "", venue: "", description: "", cover_image: "", photos: [] };
    const [form, setForm] = useState(blank);
    const [photosStr, setPhotosStr] = useState("");
    const startNew = () => { setEditing(null); setForm(blank); setPhotosStr(""); setOpen(true); };
    const startEdit = (ev) => { setEditing(ev); setForm({ ...ev }); setPhotosStr((ev.photos || []).join("\n")); setOpen(true); };
    const save = async () => {
        const data = { ...form, photos: photosStr.split("\n").map((x) => x.trim()).filter(Boolean) };
        try {
            if (editing) await e.update.mutateAsync({ id: editing.id, data });
            else await e.create.mutateAsync(data);
            toast.success("Saved");
            setOpen(false);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    return (
        <div>
            <SectionHeader title="Events" count={e.list.data?.length} onNew={startNew} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(e.list.data || []).map((ev) => (
                    <div key={ev.id} className="bg-card border border-border p-4">
                        <div className="font-eyebrow text-[10px] text-secondary">{ev.date}</div>
                        <div className="font-display text-base mt-1">{ev.name}</div>
                        <div className="text-xs text-muted-foreground">{ev.venue}</div>
                        <div className="mt-3 flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => startEdit(ev)}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="outline" onClick={() => { if (window.confirm("Delete?")) e.remove.mutate(ev.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                    </div>
                ))}
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Event</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <FormField label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></FormField>
                            <FormField label="Venue"><Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></FormField>
                        </div>
                        <FormField label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormField>
                        <FormField label="Cover image URL"><Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} /></FormField>
                        <FormField label="Photos (one per line)"><Textarea rows={2} value={photosStr} onChange={(e) => setPhotosStr(e.target.value)} /></FormField>
                    </div>
                    <DialogFooter><Button onClick={save}>Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Gallery (no update, simpler)
function GallerySection() {
    const qc = useQueryClient();
    const list = useQuery({ queryKey: ["gallery-admin"], queryFn: () => api.get("/gallery").then((r) => r.data) });
    const create = useMutation({ mutationFn: (d) => api.post("/gallery", d).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["gallery-admin"] }) });
    const remove = useMutation({ mutationFn: (id) => api.delete(`/gallery/${id}`).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["gallery-admin"] }) });
    const [open, setOpen] = useState(false);
    const blank = { title: "", category: "environment", image_url: "" };
    const [form, setForm] = useState(blank);
    const save = async () => {
        try { await create.mutateAsync(form); toast.success("Added"); setOpen(false); setForm(blank); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    return (
        <div>
            <SectionHeader title="Gallery" count={list.data?.length} onNew={() => setOpen(true)} />
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {(list.data || []).map((g) => (
                    <div key={g.id} className="relative group">
                        <img src={g.image_url} alt="" className="aspect-square w-full object-cover border border-border" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors grid place-items-center opacity-0 group-hover:opacity-100">
                            <Button size="sm" variant="destructive" onClick={() => { if (window.confirm("Delete?")) remove.mutate(g.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1 font-eyebrow capitalize">{g.category}</div>
                    </div>
                ))}
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Add gallery image</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <FormField label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormField>
                        <FormField label="Category">
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="environment">Environment</SelectItem>
                                    <SelectItem value="education">Education</SelectItem>
                                    <SelectItem value="food">Food</SelectItem>
                                    <SelectItem value="social">Social</SelectItem>
                                    <SelectItem value="events">Events</SelectItem>
                                </SelectContent>
                            </Select>
                        </FormField>
                        <FormField label="Image URL"><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></FormField>
                    </div>
                    <DialogFooter><Button onClick={save}>Add</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Blogs
function BlogsSection() {
    const qc = useQueryClient();
    const list = useQuery({ queryKey: ["blogs-admin"], queryFn: () => api.get("/blogs/admin").then((r) => r.data) });
    const create = useMutation({ mutationFn: (d) => api.post("/blogs", d).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["blogs-admin"] }) });
    const update = useMutation({ mutationFn: ({ id, data }) => api.put(`/blogs/${id}`, data).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["blogs-admin"] }) });
    const remove = useMutation({ mutationFn: (id) => api.delete(`/blogs/${id}`).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["blogs-admin"] }) });

    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const blank = { title: "", featured_image: "", content: "", excerpt: "", meta_title: "", meta_description: "", tags: [], published: true };
    const [form, setForm] = useState(blank);
    const [tagsStr, setTagsStr] = useState("");
    const startNew = () => { setEditing(null); setForm(blank); setTagsStr(""); setOpen(true); };
    const startEdit = (b) => { setEditing(b); setForm({ ...b }); setTagsStr((b.tags || []).join(", ")); setOpen(true); };
    const save = async () => {
        const data = { ...form, tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean) };
        try {
            if (editing) await update.mutateAsync({ id: editing.id, data });
            else await create.mutateAsync(data);
            toast.success("Saved");
            setOpen(false);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    return (
        <div>
            <SectionHeader title="Blog posts" count={list.data?.length} onNew={startNew} />
            <div className="space-y-3">
                {(list.data || []).map((b) => (
                    <div key={b.id} className="bg-card border border-border p-4 flex gap-4 items-center">
                        <img src={b.featured_image} alt="" className="h-16 w-24 object-cover" />
                        <div className="flex-1">
                            <div className="font-display text-base">{b.title}</div>
                            <div className="text-xs text-muted-foreground">{b.tags?.join(", ")} · {b.published ? "Published" : "Draft"}</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => startEdit(b)}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => { if (window.confirm("Delete?")) remove.mutate(b.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                ))}
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} blog post</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <FormField label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormField>
                        <FormField label="Featured image URL"><Input value={form.featured_image} onChange={(e) => setForm({ ...form, featured_image: e.target.value })} /></FormField>
                        <FormField label="Excerpt"><Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></FormField>
                        <FormField label="Content (HTML allowed)"><Textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} /></FormField>
                        <div className="grid grid-cols-2 gap-3">
                            <FormField label="Meta title"><Input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} /></FormField>
                            <FormField label="Tags (comma-separated)"><Input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} /></FormField>
                        </div>
                        <FormField label="Meta description"><Textarea rows={2} value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} /></FormField>
                        <div className="flex items-center gap-3">
                            <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
                            <span className="text-sm">Published</span>
                        </div>
                    </div>
                    <DialogFooter><Button onClick={save}>Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Volunteers
function VolunteersSection() {
    const qc = useQueryClient();
    const list = useQuery({ queryKey: ["volunteers"], queryFn: () => api.get("/volunteers").then((r) => r.data) });
    const remove = useMutation({ mutationFn: (id) => api.delete(`/volunteers/${id}`).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["volunteers"] }) });
    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl">Volunteer applications <span className="text-muted-foreground text-base">({list.data?.length || 0})</span></h2>
                <Button variant="outline" size="sm" onClick={() => csvDownload("volunteers.csv", list.data || [])}>
                    <Download className="h-4 w-4 mr-1.5" /> Export CSV
                </Button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border border-border">
                    <thead className="bg-muted/50 font-eyebrow text-[10px] text-left">
                        <tr>
                            <th className="p-3">Name</th><th className="p-3">Mobile</th><th className="p-3">Email</th>
                            <th className="p-3">Interests</th><th className="p-3">When</th><th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(list.data || []).map((v) => (
                            <tr key={v.id} className="border-t border-border">
                                <td className="p-3 font-medium">{v.full_name}</td>
                                <td className="p-3">{v.mobile}</td>
                                <td className="p-3">{v.email}</td>
                                <td className="p-3 text-xs text-muted-foreground">{(v.interests || []).join(", ")}</td>
                                <td className="p-3 text-xs">{new Date(v.created_at).toLocaleDateString()}</td>
                                <td className="p-3"><Button size="sm" variant="outline" onClick={() => { if (window.confirm("Delete?")) remove.mutate(v.id); }}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// Contacts
function ContactsSection() {
    const qc = useQueryClient();
    const list = useQuery({ queryKey: ["contacts"], queryFn: () => api.get("/contacts").then((r) => r.data) });
    const reply = useMutation({ mutationFn: (id) => api.put(`/contacts/${id}/replied`).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }) });
    const remove = useMutation({ mutationFn: (id) => api.delete(`/contacts/${id}`).then((r) => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ["contacts"] }) });
    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl">Contact messages <span className="text-muted-foreground text-base">({list.data?.length || 0})</span></h2>
                <Button variant="outline" size="sm" onClick={() => csvDownload("contacts.csv", list.data || [])}>
                    <Download className="h-4 w-4 mr-1.5" /> Export CSV
                </Button>
            </div>
            <div className="space-y-3">
                {(list.data || []).map((c) => (
                    <div key={c.id} className={`bg-card border p-4 ${c.replied ? "border-border opacity-70" : "border-secondary/40"}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <div>
                                <div className="font-display text-base">{c.subject}</div>
                                <div className="text-xs text-muted-foreground">{c.name} · {c.email} {c.mobile && `· ${c.mobile}`}</div>
                            </div>
                            <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</div>
                        </div>
                        <p className="text-sm leading-relaxed">{c.message}</p>
                        <div className="mt-3 flex gap-2">
                            {!c.replied && (
                                <Button size="sm" variant="outline" onClick={() => reply.mutate(c.id)}><Reply className="h-3.5 w-3.5 mr-1" /> Mark replied</Button>
                            )}
                            <a href={`mailto:${c.email}?subject=Re: ${encodeURIComponent(c.subject)}`}>
                                <Button size="sm" variant="outline"><Mail className="h-3.5 w-3.5 mr-1" /> Email</Button>
                            </a>
                            <Button size="sm" variant="outline" onClick={() => { if (window.confirm("Delete?")) remove.mutate(c.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Settings
function SettingsSection() {
    const qc = useQueryClient();
    const list = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
    const update = useMutation({
        mutationFn: (data) => api.put("/settings", data).then((r) => r.data),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Settings saved"); },
        onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
    });
    const [form, setForm] = useState(null);
    React.useEffect(() => { if (list.data && !form) setForm({ ...list.data }); }, [list.data]); // eslint-disable-line
    if (!form) return <div className="text-muted-foreground">Loading…</div>;
    const set = (k, v) => setForm({ ...form, [k]: v });
    return (
        <div className="space-y-6">
            <h2 className="font-display text-2xl">Site settings</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-card border border-border p-5 space-y-3">
                    <h3 className="font-eyebrow text-xs text-secondary">Hero</h3>
                    <FormField label="Hero title"><Textarea rows={2} value={form.hero_title} onChange={(e) => set("hero_title", e.target.value)} /></FormField>
                    <FormField label="Hero subtitle"><Textarea rows={3} value={form.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} /></FormField>
                    <FormField label="Hero image URL"><Input value={form.hero_image} onChange={(e) => set("hero_image", e.target.value)} /></FormField>
                </div>

                <div className="bg-card border border-border p-5 space-y-3">
                    <h3 className="font-eyebrow text-xs text-secondary">About</h3>
                    <FormField label="About short"><Textarea rows={3} value={form.about_short} onChange={(e) => set("about_short", e.target.value)} /></FormField>
                    <FormField label="Vision"><Textarea rows={2} value={form.vision} onChange={(e) => set("vision", e.target.value)} /></FormField>
                    <FormField label="Mission"><Textarea rows={2} value={form.mission} onChange={(e) => set("mission", e.target.value)} /></FormField>
                </div>

                <div className="bg-card border border-border p-5 grid grid-cols-2 gap-3">
                    <h3 className="font-eyebrow text-xs text-secondary col-span-2">Impact counters</h3>
                    <FormField label="Beneficiaries"><Input type="number" value={form.counter_beneficiaries} onChange={(e) => set("counter_beneficiaries", Number(e.target.value))} /></FormField>
                    <FormField label="Trees"><Input type="number" value={form.counter_trees} onChange={(e) => set("counter_trees", Number(e.target.value))} /></FormField>
                    <FormField label="Food kits"><Input type="number" value={form.counter_food_kits} onChange={(e) => set("counter_food_kits", Number(e.target.value))} /></FormField>
                    <FormField label="Programs"><Input type="number" value={form.counter_programs} onChange={(e) => set("counter_programs", Number(e.target.value))} /></FormField>
                </div>

                <div className="bg-card border border-border p-5 space-y-3">
                    <h3 className="font-eyebrow text-xs text-secondary">Donation</h3>
                    <FormField label="UPI ID"><Input value={form.upi_id} onChange={(e) => set("upi_id", e.target.value)} /></FormField>
                    <FormField label="UPI QR image URL"><Input value={form.upi_qr_image} onChange={(e) => set("upi_qr_image", e.target.value)} /></FormField>
                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="Bank name"><Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} /></FormField>
                        <FormField label="Account name"><Input value={form.account_name} onChange={(e) => set("account_name", e.target.value)} /></FormField>
                        <FormField label="Account number"><Input value={form.account_number} onChange={(e) => set("account_number", e.target.value)} /></FormField>
                        <FormField label="IFSC"><Input value={form.ifsc} onChange={(e) => set("ifsc", e.target.value)} /></FormField>
                        <FormField label="Branch"><Input value={form.branch} onChange={(e) => set("branch", e.target.value)} /></FormField>
                    </div>
                    <FormField label="Donation note"><Textarea rows={2} value={form.donation_note} onChange={(e) => set("donation_note", e.target.value)} /></FormField>
                </div>

                <div className="bg-card border border-border p-5 space-y-3 lg:col-span-2">
                    <h3 className="font-eyebrow text-xs text-secondary">Contact & Social</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <FormField label="Address"><Textarea rows={2} value={form.contact_address} onChange={(e) => set("contact_address", e.target.value)} /></FormField>
                        <FormField label="Google Maps embed URL"><Textarea rows={2} value={form.google_maps_embed} onChange={(e) => set("google_maps_embed", e.target.value)} /></FormField>
                        <FormField label="Phone"><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></FormField>
                        <FormField label="Email"><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></FormField>
                        <FormField label="WhatsApp number (intl)"><Input value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} /></FormField>
                        <FormField label="Facebook URL"><Input value={form.facebook_url} onChange={(e) => set("facebook_url", e.target.value)} /></FormField>
                        <FormField label="Instagram URL"><Input value={form.instagram_url} onChange={(e) => set("instagram_url", e.target.value)} /></FormField>
                        <FormField label="Twitter URL"><Input value={form.twitter_url} onChange={(e) => set("twitter_url", e.target.value)} /></FormField>
                        <FormField label="Youtube URL"><Input value={form.youtube_url} onChange={(e) => set("youtube_url", e.target.value)} /></FormField>
                    </div>
                </div>
            </div>
            <Button onClick={() => update.mutate(form)} className="rounded-full bg-primary hover:bg-primary/90 h-11 px-6" data-testid="settings-save">
                Save settings
            </Button>
        </div>
    );
}

// --- shared subcomponents ---
function FormField({ label, children }) {
    return (
        <div>
            <Label className="font-eyebrow text-[10px] text-muted-foreground">{label}</Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
function SectionHeader({ title, count, onNew }) {
    return (
        <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl">{title} <span className="text-muted-foreground text-base">({count || 0})</span></h2>
            <Button onClick={onNew} className="rounded-full bg-primary hover:bg-primary/90" data-testid={`new-${title.toLowerCase()}`}>
                <Plus className="h-4 w-4 mr-1" /> New
            </Button>
        </div>
    );
}

// ============================================================================
// MAIN DASHBOARD
// ============================================================================
export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const handleLogout = () => { logout(); nav("/admin/login"); };
    return (
        <div className="min-h-screen bg-muted/20" data-testid="admin-dashboard">
            <header className="bg-background border-b border-border sticky top-0 z-30">
                <div className="mx-auto max-w-7xl px-5 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="h-8 w-8 rounded-sm bg-primary text-primary-foreground grid place-items-center"><Leaf className="h-4 w-4" /></span>
                        <span className="font-display text-base">Tishya Admin</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground hidden sm:inline">{user?.email}</span>
                        <Link to="/" target="_blank"><Button variant="outline" size="sm">View site</Button></Link>
                        <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout">
                            <LogOut className="h-4 w-4 mr-1" /> Logout
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-5 lg:px-10 py-8">
                <div className="mb-8">
                    <div className="font-eyebrow text-xs text-secondary mb-2">Dashboard</div>
                    <h1 className="font-display text-3xl lg:text-4xl">Welcome back, {user?.name || "Admin"}</h1>
                </div>

                <StatsSection />

                <Tabs defaultValue="trustees" className="mt-10">
                    <TabsList className="bg-background border border-border rounded-none h-auto p-1 flex-wrap justify-start">
                        <TabsTrigger value="trustees" data-testid="tab-trustees">Trustees</TabsTrigger>
                        <TabsTrigger value="programs" data-testid="tab-programs">Programs</TabsTrigger>
                        <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
                        <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
                        <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
                        <TabsTrigger value="blogs" data-testid="tab-blogs">Blog</TabsTrigger>
                        <TabsTrigger value="volunteers" data-testid="tab-volunteers">Volunteers</TabsTrigger>
                        <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="tab-settings">Site</TabsTrigger>
                    </TabsList>
                    <div className="mt-8">
                        <TabsContent value="trustees"><TrusteesSection /></TabsContent>
                        <TabsContent value="programs"><ProgramsSection /></TabsContent>
                        <TabsContent value="projects"><ProjectsSection /></TabsContent>
                        <TabsContent value="events"><EventsSection /></TabsContent>
                        <TabsContent value="gallery"><GallerySection /></TabsContent>
                        <TabsContent value="blogs"><BlogsSection /></TabsContent>
                        <TabsContent value="volunteers"><VolunteersSection /></TabsContent>
                        <TabsContent value="contacts"><ContactsSection /></TabsContent>
                        <TabsContent value="settings"><SettingsSection /></TabsContent>
                    </div>
                </Tabs>
            </main>
        </div>
    );
}
